// Markets API - Real Backend Integration
import { createPublicClient, OrderSide } from '@polymarket/client';

import { mockMarkets } from '@/data';
import type {
  EventResponse,
  EventsListResponse,
  Market,
  MarketsQueryParams,
  OrderBook,
  PaginatedResponse,
  UserPnlResponse,
} from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

// Public (unauthenticated) client for orderbook/price reads. @polymarket/client
// defaults to the `production` environment (https://clob.polymarket.com), which
// matches CLOB_API_URL's default; no host/chain args needed.
const publicClobClient = createPublicClient();

export interface TradePayload {
  outcomeId: string;
  amount: number;
  isBuy: boolean;
  limitPrice?: number;
}

export interface TradeResponse {
  id: string;
  marketId: string;
  outcomeId: string;
  amount: number;
  price: number;
  isBuy: boolean;
  createdAt: string;
}

export const marketsApi = {
  /**
   * Get all markets with optional filtering
   * Note: Markets are fetched from the tags endpoint based on category slug
   * Default to 'trending' if no category is specified
   */
  getAll: async (params?: MarketsQueryParams): Promise<Market[]> => {
    // Default params
    const defaultParams: MarketsQueryParams = {
      mode: 'normalized',
      status: 'active',
      limit: 20,
      offset: 0,
    };

    const finalParams = { ...defaultParams, ...params };

    // Clean up category/tag for specific slugs
    const categoryLower = finalParams.category?.toLowerCase();

    // If category is "All", treat it as a tag slug "all" instead of a category filter
    if (categoryLower === 'all') {
      finalParams.tag_slug = 'all';
      delete finalParams.category;
    } else if (!categoryLower || categoryLower === 'trending' || categoryLower === 'new') {
      delete finalParams.category;
      delete finalParams.tag;
    }

    // Explicitly handle "all" tag_slug passed directly
    if (finalParams.tag_slug === 'all') {
      // If tag_slug is 'all', we might want to ensure category is cleared so it searches broadly
      // or respect the category if it exists (e.g. "Sports" -> "All").
      // Based on user request, if "all" tag is active, it seems we want to pass that.
      // However, usually 'all' means 'no tag filter'.
      // If the backend specifically expects 'all' as a tag to return everything in a category, keep it.
      // If 'all' means 'remove tag filter', we should delete it.
      // The user said: "why you dont pass tag_slug in market api when you you are all tag you have to pass category slug"
      // This implies if tag is 'all', we SHOULD pass it, possibly alongside category.
      // So we do nothing here, letting 'all' be passed as tag_slug.
    }

    // Map 'page' to 'offset' if needed (though getPaginated handles this better)
    if (finalParams.page && finalParams.limit) {
      finalParams.offset = (finalParams.page - 1) * finalParams.limit;
      delete finalParams.page; // Remove page to avoid API confusions if it doesn't support it
    }

    try {
      const response = await axiosClient.get(API_ENDPOINTS.MARKETS_BASE, {
        params: finalParams,
      });

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return mockMarkets;
    } catch (error) {
      console.warn('API fetch failed, falling back to mock data');
      return mockMarkets;
    }
  },

  /**
   * getPaginated: Fetches paginated event data from the standard markets endpoint.
   * Maps UI-level 'MarketsQueryParams' to the specific parameters expected by the backend.
   */
  getPaginated: async (params?: MarketsQueryParams): Promise<PaginatedResponse<EventResponse>> => {
    const limit = params?.limit || 20; // Default requested limit
    // Allow offset to be passed directly, or calculate from page
    const offset = params?.offset !== undefined ? params.offset : ((params?.page || 1) - 1) * limit;
    // Calculate current page for response metadata
    const page = Math.floor(offset / limit) + 1;

    // Build params for new events API
    const apiParams: any = {
      limit,
      offset,
    };

    // Add tag_slug if provided
    if (params?.tag_slug) {
      apiParams.tag_slug = params.tag_slug;
    }

    // Add other filters if needed
    if (params?.active !== undefined) {
      apiParams.active = params.active;
    }
    if (params?.closed !== undefined) {
      apiParams.closed = params.closed;
    }
    if (params?.sortBy) {
      apiParams.order = params.sortBy;
    }
    if (params?.range) {
      apiParams.range = params.range;
    }
    if (params?.sortOrder) {
      apiParams.ascending = params.sortOrder === 'asc';
    } else if (params?.ascending !== undefined) {
      apiParams.ascending = params.ascending;
    }

    if (params?.recurrence) {
      apiParams.recurrence = params.recurrence;
    }
    if (params?.tag_id) {
      apiParams.tag_id = params.tag_id;
    }
    if (params?.exclude_tag_id) {
      apiParams.exclude_tag_id = params.exclude_tag_id;
    }
    if (params?.archived !== undefined) {
      apiParams.archived = params.archived;
    }
    if (params?.start_date) {
      apiParams.start_date = params.start_date;
    }
    if (params?.start_date_min) {
      apiParams.start_date_min = params.start_date_min;
    }
    if (params?.end_date) {
      apiParams.end_date = params.end_date;
    }
    if (params?.end_date_min) {
      apiParams.end_date_min = params.end_date_min;
    }
    if (params?.end_date_max) {
      apiParams.end_date_max = params.end_date_max;
    }

    if (params?.search) {
      apiParams.search = params.search;
    }

    if (params?.series_id) {
      apiParams.series_id = params.series_id;
    }

    if (params?.sport_type !== undefined) {
      apiParams.sport_type = params.sport_type;
    }

    if (params?.hideSport !== undefined) {
      apiParams.hideSport = params.hideSport;
    }

    if (params?.live !== undefined) {
      apiParams.live = params.live;
    }

    if (params?.order) {
      apiParams.order = params.order;
    }

    try {
      const response = await axiosClient.get<EventsListResponse>(
        API_ENDPOINTS.MARKETS_EVENTS_LIST,
        {
          params: apiParams,
        },
      );

      if (response && response.data?.success && Array.isArray(response.data?.data)) {
        // Return events directly
        const events = response.data.data;
        const total = events.length;
        const hasNextPage = events.length >= limit;

        return {
          items: events,
          total: total,
          page,
          limit,
          totalPages: hasNextPage ? page + 1 : page,
          hasNextPage: hasNextPage,
          hasPrevPage: page > 1,
        };
      }

      throw new Error('Invalid API response');
    } catch (e) {
      console.error('Paginated API fetch failed:', e);
      throw e;
    }
  },

  /**
   * Get single market by ID
   */
  getById: async (id: string): Promise<EventResponse> => {
    // Search across all mock data sources
    // Fallback to API call if not found in mock data
    try {
      // const isNumericId = /^\d+$/.test(id);

      // // If it's numeric, assume it's a direct Market ID
      // if (isNumericId) {
      //   const response = await axiosClient.get(API_ENDPOINTS.MARKETS_BY_ID(id));
      //   if (response.data?.success && response.data?.data) {
      //     return response.data.data;
      //   }
      //   if (response.data) {
      //     return response.data;
      //   }
      // } else {
      // If it's a slug, fetch the Event then return it
      const response = await axiosClient.get(API_ENDPOINTS.MARKETS_EVENT_BY_SLUG(id));
      if (response.data?.success && response.data?.data) {
        return response.data.data;
        // }
      }
    } catch (error) {
      // If API call fails and market not found in mock data, continue to throw
      console.error(`Failed to fetch market for id/slug "${id}"`, error);
    }

    throw new Error(`Market with id "${id}" not found`);
  },

  /**
   * Create new market
   */
  create: async (marketData: Omit<Market, 'id'>): Promise<Market> => {
    const response = await axiosClient.post(API_ENDPOINTS.MARKETS_BASE, marketData);

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }

    return response.data;
  },

  /**
   * Update existing market
   */
  update: async (id: string, marketData: Partial<Market>): Promise<Market> => {
    const response = await axiosClient.put(API_ENDPOINTS.MARKETS_BY_ID(id), marketData);

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }

    return response.data;
  },

  /**
   * Delete market
   */
  delete: async (id: string): Promise<void> => {
    await axiosClient.delete(API_ENDPOINTS.MARKETS_BY_ID(id));
  },

  /**
   * Execute a trade
   */
  trade: async (marketId: string, payload: TradePayload): Promise<TradeResponse> => {
    const response = await axiosClient.post(API_ENDPOINTS.MARKETS_TRADE(marketId), payload);

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }

    return response.data;
  },

  /**
   * Get breaking markets (markets with biggest price changes in 24h)
   */
  getBreaking: async (): Promise<Market[]> => {
    // Use the 'breaking' category slug
    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_TAGS_BREAKING);

    if (response.data?.success && response.data?.data?.markets) {
      return response.data.data.markets;
    }

    return response.data?.markets || [];
  },

  /**
   * Get all events for card type detection
   */
  getEvents: async (): Promise<any[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.EVENTS);
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  },

  /**
   * Get realtime prices for specific clobTokenIds array
   */
  getMarketPrices: async (tokenIds: string[]): Promise<Record<string, any>> => {
    const priceMap: Record<string, any> = {};
    if (!tokenIds || !tokenIds.length) return priceMap;

    await Promise.all(
      tokenIds.map(async (tokenId: string) => {
        try {
          // fetchPrice returns the price as a DecimalString directly (the old
          // getPrice returned an object with a `.price` field).
          const [bidResponse, askResponse] = await Promise.all([
            publicClobClient.fetchPrice({ tokenId, side: OrderSide.BUY }),
            publicClobClient.fetchPrice({ tokenId, side: OrderSide.SELL }),
          ]);

          const bidPrice = parseFloat(bidResponse);
          const askPrice = parseFloat(askResponse);

          if (
            !isNaN(bidPrice) &&
            !isNaN(askPrice) &&
            bidPrice > 0 &&
            bidPrice < 1 &&
            askPrice > 0 &&
            askPrice < 1
          ) {
            priceMap[tokenId] = {
              bidPrice,
              askPrice,
              midPrice: (bidPrice + askPrice) / 2,
              spread: askPrice - bidPrice,
            };
          }
        } catch (error) {
          console.warn(`Error fetching price for token ${tokenId}:`, error);
        }
      }),
    );
    return priceMap;
  },

  /**
   * Get tick size for a specific token
   */
  getTickSize: async (tokenId: string): Promise<string> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.CLOB.TICK_SIZE, {
        params: { token_id: tokenId },
      });
      return response.data;
    } catch (error) {
      console.warn(`Error fetching tick size for token ${tokenId}:`, error);
      return '0.1'; // Fallback
    }
  },

  /**
   * Get order book for a specific token
   */
  getOrderBook: async (tokenId: string): Promise<OrderBook> => {
    try {
      const response = await axiosClient.get<OrderBook>(API_ENDPOINTS.CLOB.BOOK, {
        params: { token_id: tokenId },
      });
      return response.data;
    } catch (error) {
      console.warn(`Error fetching order book for token ${tokenId}:`, error);
      return { bids: [], asks: [], hash: '' }; // Fallback
    }
  },

  /**
   * Get user PNL and timeline
   */
  getUserPnl: async (
    userAddress: string,
    interval?: string,
    fidelity: string = '1d',
  ): Promise<UserPnlResponse> => {
    const params: Record<string, string> = {
      user_address: userAddress,
      fidelity,
    };

    if (interval) {
      params.interval = interval;
    }

    const response = await axiosClient.get<UserPnlResponse>(API_ENDPOINTS.MARKETS_USER_PNL, {
      params,
    });
    return response.data;
  },

  /**
   * Translate market rules
   */
  translateMarketRule: async (text: string): Promise<{ translatedText: string }> => {
    const response = await axiosClient.post(API_ENDPOINTS.MARKETS_RULE_TRANSLATION, {
      text,
    });
    return response.data;
  },
};
